<template>
    <div class="tm-scope">
        <!-- Hero: headline + action on the left, the payoff running live on the right.
             The explainer copy moved down to "How it works", where it belongs. -->
        <section class="tp-hero">
            <div class="tm-gridfield"></div>
            <div class="tm-container tp-hero__inner">
                <div class="tp-hero__grid">
                    <div class="tp-hero__lead">
                        <h1 class="tm-hero-title tm-reveal" style="animation-delay: 60ms">
                            Find the roles your skills already
                            <span class="tm-gradient-text">qualify you for.</span>
                        </h1>

                        <div class="tp-entry tm-reveal" style="animation-delay: 140ms">
                            <div class="tp-entry__row">
                                <input type="text" class="tp-entry__input" placeholder="Job title, keyword..." v-model="jobTitle">
                                <SuggestionDropdown :items="jobs" label-key="name" :visible="showdropdown" @select="setJobTitle" />
                                <div class="tp-entry__divider"></div>
                                <button type="button" class="tp-entry__upload" data-bs-toggle="modal" data-bs-target="#exampleModal">
                                    <span v-if="!preview">Upload your CV</span>
                                    <span v-else>{{ preview }}</span>
                                </button>
                                <button class="tm-btn-primary tp-entry__go" @click="uploadCv()">My career path</button>
                            </div>
                        </div>
                    </div>

                    <div class="tp-hero__panel">
                        <div class="tp-panel-glow"></div>
                        <div class="tp-translate tm-reveal" style="animation-delay: 260ms">
                            <div class="tp-translate__bar">
                                <span class="tp-translate__dot"></span>
                                <span class="tp-translate__dot"></span>
                                <span class="tp-translate__dot"></span>
                                <span class="tp-translate__file tm-mono">your-cv.pdf</span>
                                <span class="tm-chip tm-chip--accent tp-translate__badge">Readiness</span>
                            </div>

                            <div class="tp-translate__body">
                                <div
                                    v-for="(role, i) in roleMap" :key="role.name"
                                    class="tp-rrow" :class="{ 'is-resolved': resolved > i }"
                                >
                                    <span class="tp-rrow__name">{{ role.name }}</span>
                                    <span class="tm-meter tp-rrow__meter">
                                        <span class="tm-meter__fill"
                                              :style="{ width: (resolved > i ? role.pct : 0) + '%',
                                                        background: readyColor(role.pct) }"></span>
                                    </span>
                                    <span class="tp-rrow__pct tm-mono"
                                          :style="{ color: readyColor(role.pct) }">{{ role.pct }}%</span>
                                </div>
                            </div>

                            <div class="tp-translate__foot">
                                <span class="tm-chip tm-chip--gap">4 skills away</span>
                                <span class="tp-translate__foot-text">from Data Scientist</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Scale, as a quiet strip rather than four cards competing with the hero -->
        <section class="tp-kpis">
            <div class="tm-container tp-kpis__grid">
                <div class="tp-kpi">
                    <span class="tp-kpi__num tm-mono">{{ stats.jobs }}</span>
                    <span class="tp-kpi__label">Job titles mapped</span>
                </div>
                <div class="tp-kpi">
                    <span class="tp-kpi__num tm-mono">{{ stats.skills }}</span>
                    <span class="tp-kpi__label">Skills &amp; knowledge</span>
                </div>
                <div class="tp-kpi">
                    <span class="tp-kpi__num tm-mono">{{ stats.users }}</span>
                    <span class="tp-kpi__label">People mapped</span>
                </div>
                <div class="tp-kpi">
                    <span class="tp-kpi__num tm-mono">{{ stats.careers }}</span>
                    <span class="tp-kpi__label">Career paths built</span>
                </div>
            </div>
        </section>

        <!-- Why Topment -->
        <section class="tm-section">
            <div class="tm-container tm-container--text tp-sec__head">
                <h2 class="tm-h2 text-center tm-reveal">Why Topment</h2>
            </div>
            <div class="tm-container">
                <div class="tp-cards">
                    <div class="tp-vcard tm-reveal" v-for="(item, i) in whyItems" :key="item.title"
                         :style="{ animationDelay: (i * 60) + 'ms' }">
                        <span class="tm-icon-tile tp-vcard__icon"><FontAwesomeIcon :icon="item.icon" /></span>
                        <h3 class="tp-vcard__title">{{ item.title }}</h3>
                        <p class="tp-vcard__text">{{ item.text }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- How it works: the centerpiece. Full-width story beats, each one showing
             what the user actually sees at that step. -->
        <section class="tm-section tm-section--sunk tp-how">
            <div class="tm-container tm-container--text tp-sec__head">
                <h2 class="tm-h2 text-center tm-reveal">How it works</h2>
            </div>
            <div class="tm-container">
                <div class="tp-beats">
                    <article class="tp-beat tm-reveal">
                        <div class="tp-beat__side">
                            <span class="tp-beat__num tm-mono">01</span>
                            <h3 class="tp-beat__name">You hand us your CV</h3>
                            <p>
                                A PDF, a Word file, even a photo of a printed one. Nothing to fill in
                                and no form to rewrite your history into.
                            </p>
                        </div>
                        <div class="tp-beat__demo">
                            <div class="tp-demo tp-demo--drop">
                                <FontAwesomeIcon icon="fa-solid fa-file-arrow-up" class="tp-demo__icon" />
                                <span class="tp-demo__file tm-mono">amelie-rousseau-cv.pdf</span>
                                <span class="tp-demo__sub">3 pages &nbsp;·&nbsp; 214 KB</span>
                            </div>
                        </div>
                    </article>

                    <article class="tp-beat tp-beat--flip tm-reveal">
                        <div class="tp-beat__side">
                            <span class="tp-beat__num tm-mono">02</span>
                            <h3 class="tp-beat__name">We translate it into the market's language</h3>
                            <p>
                                Your phrasing on the left, the standard term recruiters and applicant
                                tracking systems search for on the right. You confirm anything we were
                                unsure about.
                            </p>
                        </div>
                        <div class="tp-beat__demo">
                            <div class="tp-demo">
                                <div class="tp-demo__line" v-for="pair in translateDemo" :key="pair.term">
                                    <span class="tm-raw tp-demo__raw">"{{ pair.raw }}"</span>
                                    <span class="tp-demo__arrow">&rarr;</span>
                                    <span class="tm-mono tp-demo__term">{{ pair.term }}</span>
                                </div>
                            </div>
                        </div>
                    </article>

                    <article class="tp-beat tm-reveal">
                        <div class="tp-beat__side">
                            <span class="tp-beat__num tm-mono">03</span>
                            <h3 class="tp-beat__name">You see where you actually stand</h3>
                            <p>
                                Every role scored against your skills, weighted by what matters. Often
                                the surprise is a role you never considered sitting higher than the one
                                you asked about.
                            </p>
                        </div>
                        <div class="tp-beat__demo">
                            <div class="tp-demo">
                                <div class="tp-demo__role" v-for="role in roleMap.slice(0, 3)" :key="role.name">
                                    <span class="tp-demo__role-name">{{ role.name }}</span>
                                    <span class="tm-meter tp-demo__meter">
                                        <span class="tm-meter__fill"
                                              :style="{ width: role.pct + '%', background: readyColor(role.pct) }"></span>
                                    </span>
                                    <span class="tm-mono tp-demo__pct"
                                          :style="{ color: readyColor(role.pct) }">{{ role.pct }}%</span>
                                </div>
                            </div>
                        </div>
                    </article>

                    <article class="tp-beat tp-beat--flip tm-reveal">
                        <div class="tp-beat__side">
                            <span class="tp-beat__num tm-mono">04</span>
                            <h3 class="tp-beat__name">You get an order to work in</h3>
                            <p>
                                The gap, ranked. Not a word cloud of everything you lack, but the short
                                list that moves your readiness the most, hardest-hitting first.
                            </p>
                        </div>
                        <div class="tp-beat__demo">
                            <div class="tp-demo">
                                <div class="tp-demo__gap" v-for="(g, i) in gapDemo" :key="g.name">
                                    <span class="tp-demo__rank tm-mono">{{ String(i + 1).padStart(2, '0') }}</span>
                                    <span class="tp-demo__gap-name">{{ g.name }}</span>
                                    <span class="tm-chip" :class="g.has ? 'tm-chip--have' : 'tm-chip--gap'">
                                        {{ g.has ? 'You have it' : 'Missing' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>

                <div class="tp-how__cta tm-reveal">
                    <button class="tm-btn-primary" @click="goToEntry()">Map my career</button>
                </div>
            </div>
        </section>

        <!-- Most popular jobs: the most-posted occupations, each opening its page -->
        <section class="tm-section" v-if="popularJobs.length">
            <div class="tm-container">
                <div class="tp-sec__bar tm-reveal">
                    <h2 class="tm-h2">Most popular jobs</h2>
                    <router-link class="tm-btn-secondary tp-sec__all" :to="{ name: 'occupations' }">
                        View all <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                    </router-link>
                </div>
                <div class="tm-job-grid mt-5">
                    <router-link class="tm-row tm-job-row" v-for="job in popularJobs" :key="job.occupation_id"
                                 :to="{ name: 'occupation', params: { id: job.occupation_id } }">
                        <span class="tm-job-row__name">{{ job.name }}</span>
                        <span class="tm-mono tm-muted">{{ job.postings.toLocaleString() }} postings</span>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- Popular category -->
        <section class="tm-section" style="background: var(--tm-canvas-raised);" v-if="categories.length">
            <div class="tm-container">
                <div class="tp-sec__bar tm-reveal">
                    <h2 class="tm-h2">Popular categories</h2>
                </div>
                <div class="tm-category-grid mt-5">
                    <router-link class="tm-card tm-card--interactive tm-category tm-reveal" v-for="(cat, i) in categories" :key="cat.name"
                                 :to="{ name: 'occupations', query: { category: cat.name } }" :style="{ animationDelay: (i * 40) + 'ms' }">
                        <span class="tm-tag tm-category__icon" :class="'tm-tag--' + PASTELS[i % PASTELS.length]"><FontAwesomeIcon :icon="categoryIcon(cat.name)" /></span>
                        <div class="ms-3">
                            <h6 class="fw-bold mb-0">{{ cat.name }}</h6>
                            <span class="tm-muted" style="font-size: 0.82rem;">{{ cat.occupations.toLocaleString() }} occupations</span>
                        </div>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- Become an employer: one banner, real copy, no fake working CTA -->
        <section class="tm-section tm-section--tight">
            <div class="tm-container tm-container--text">
                <div class="tm-card tm-employer tm-reveal">
                    <span class="tm-tag tm-tag--blue">For employers</span>
                    <h3 class="mt-3">Hiring is coming to Topment</h3>
                    <p class="tm-body">We are opening employer accounts in a future release. Reach out and we will let you know when it is ready.</p>
                    <a class="tm-btn-primary mt-2" href="mailto:admin@topment.fr" style="text-decoration: none;">Get notified</a>
                </div>
            </div>
        </section>

        <Footer/>

        <!-- Modal -->
        <div class="modal fade" id="exampleModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content tm-modal">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5 tm-serif" id="exampleModalLabel">CV upload</h1>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body text-center">
                        <div>
                            <div
                            class="tm-dropzone"
                              @dragover.prevent
                              @dragenter.prevent
                              @drop="handleDrop($event)"
                            >
                            <div v-if="preview">
                                <FontAwesomeIcon icon="fa-solid fa-file-upload" class="tm-dropzone__icon"></FontAwesomeIcon>
                                <div class="mt-2">{{ preview }}</div>
                            </div>
                            <div v-else>
                                <p class="tm-muted mb-0">Drag and drop the file here</p>
                            </div>
                        </div>
                        <div v-if="error" class="text-danger mt-2">{{ error }}</div>
                        <input type="file" ref="fileInput" @change="handleFileSelect($event)"
                                accept="application/pdf,application/msword,image/jpeg"/>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button id="close" type="button" class="tm-btn-secondary" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
    faArrowRight, faMagnifyingGlass, faFileUpload, faBriefcase, faLayerGroup, faUsers, faRoute,
    faWandMagicSparkles, faChartLine, faGlobe, faShieldHalved, faUserTie, faHeart, faStar,
    faPenNib, faCode, faChartSimple, faSuitcaseMedical, faDatabase, faFlask, faGears, faGraduationCap,
    faScaleBalanced, faStore, faHeadset, faIndustry,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { defineComponent, ref, reactive, watch, onMounted, onUnmounted } from 'vue';
import Footer from '../../components/footer.vue';
import SuggestionDropdown from '@/components/SuggestionDropdown.vue';
import { useRouter } from 'vue-router';
import { CareerApiService } from '@/services/CareerApiService';
import { OccupationApiService } from '@/services/OccupationApiService';

const PASTELS = ['blue', 'green', 'yellow', 'red'];
// Category (data_pipeline/categories.py ISCO labels) -> icon; anything else gets the briefcase.
const CATEGORY_ICONS = {
    'Software & IT': 'fa-solid fa-code', 'IT technicians': 'fa-solid fa-database',
    'Science & engineering': 'fa-solid fa-flask', 'Engineering technicians': 'fa-solid fa-gears',
    'Business & finance': 'fa-solid fa-chart-simple', 'Business support': 'fa-solid fa-chart-line',
    'Health': 'fa-solid fa-suitcase-medical', 'Health support': 'fa-solid fa-suitcase-medical',
    'Teaching': 'fa-solid fa-graduation-cap', 'Law, social & culture': 'fa-solid fa-scale-balanced',
    'Legal, social & creative support': 'fa-solid fa-pen-nib', 'Sales': 'fa-solid fa-store',
    'Business management': 'fa-solid fa-user-tie', 'Production & services management': 'fa-solid fa-industry',
    'Hospitality & retail management': 'fa-solid fa-store', 'Customer service': 'fa-solid fa-headset',
};

export default defineComponent({
    components:{FontAwesomeIcon,Footer,SuggestionDropdown},
    setup() {
        library.add(faMagnifyingGlass,faArrowRight,faFileUpload,faBriefcase,faLayerGroup,faUsers,faRoute,
                    faWandMagicSparkles,faChartLine,faGlobe,faShieldHalved,faUserTie,faHeart,faStar,
                    faPenNib,faCode,faChartSimple,faSuitcaseMedical,faDatabase,faFlask,faGears,faGraduationCap,
                    faScaleBalanced,faStore,faHeadset,faIndustry)
        const router = useRouter();
        const selectedFile = ref(null)
        const preview = ref(null);
        const spinner = ref(false);
        const jobTitle = ref();
        const jobId = ref();
        const showdropdown = ref(false);
        const jobs = ref([]);
        const stats = reactive({ jobs: '-', skills: '-', users: '-', careers: '-' });

        // Hero panel: the career-mapping payoff — roles scored by readiness,
        // resolving one by one. Same shape the dashboard renders for real.
        const roleMap = [
            { name: 'Data Analyst', pct: 88 },
            { name: 'Business Intelligence Analyst', pct: 81 },
            { name: 'Data Scientist', pct: 69 },
            { name: 'Machine Learning Engineer', pct: 47 },
            { name: 'AI Engineer', pct: 44 },
            { name: 'Product Analyst', pct: 41 },
        ];

        const translateDemo = [
            { raw: 'wrangling messy spreadsheets', term: 'data cleansing' },
            { raw: 'built dashboards for the sales team', term: 'business intelligence' },
            { raw: 'ran A/B tests on pricing', term: 'statistical analysis' },
        ];

        const whyItems = [
            { icon: 'fa-solid fa-layer-group', title: 'One shared vocabulary',
              text: 'Every skill and job title is mapped onto ESCO, the taxonomy European employers and hiring systems already index against.' },
            { icon: 'fa-solid fa-chart-line', title: 'Scores you can act on',
              text: 'Readiness is weighted by how much each skill matters to the role, so the ranking tells you what to learn first.' },
            { icon: 'fa-solid fa-globe', title: 'Every field, not just tech',
              text: 'From the most talked-about occupations to the underrated ones. The taxonomy covers all of them equally.' },
            { icon: 'fa-solid fa-shield-halved', title: 'Your data stays yours',
              text: 'No third-party marketing partners and no selling your history to recruiters. Your CV is your business.' },
            { icon: 'fa-solid fa-database', title: 'Built on published data, not opinion',
              text: 'We extract knowledge from real job postings across sources, not received wisdom, so every score reflects what employers are actually hiring for right now.' },
            { icon: 'fa-solid fa-heart', title: 'Free for individuals',
              text: 'We built this for people managing their own careers, not for the companies hiring them.' },
        ];

        const gapDemo = [
            { name: 'machine learning', has: false },
            { name: 'data mining', has: false },
            { name: 'statistical analysis', has: true },
        ];

        function readyColor(pct) {
            if (pct >= 75) return 'var(--tm-have)'
            if (pct >= 45) return 'var(--tm-partial)'
            return 'var(--tm-gap)'
        }
        const resolved = ref(0);
        let heroTimers = [];

        const popularJobs = ref([]);
        const categories = ref([]);

        onMounted(() => {
            CareerApiService.PlatformStats().then((res) => {
                const d = res.data;
                stats.jobs = Number(d.unique_jobs).toLocaleString();
                stats.skills = Number(d.unique_skills).toLocaleString();
                stats.users = Number(d.unique_users).toLocaleString();
                stats.careers = Number(d.total_careers).toLocaleString();
            }).catch(() => {});
            OccupationApiService.Highlights().then(res => {
                popularJobs.value = res.data.jobs;
                categories.value = res.data.categories;
            }).catch(() => {});

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                resolved.value = roleMap.length;
            } else {
                roleMap.forEach((_, i) => {
                    heroTimers.push(setTimeout(() => { resolved.value = i + 1 }, 600 + i * 520));
                });
            }
        });

        onUnmounted(() => heroTimers.forEach(clearTimeout));

        function handleFileSelect(event) {
          const file = event.target.files[0];
          if (file) {
            selectedFile.value = file;
            preview.value = file.name; // ایجاد پیش‌نمایش
            document.getElementById('close').click();
          }
        }

        function handleDrop(event) {
          event.preventDefault();

          const files = event.dataTransfer?.files;
          if (files && files.length > 0) {
            console.log(files[0]);

            selectedFile.value = files[0];
            preview.value = files[0].name; // ایجاد پیش‌نمایش
            console.log(preview.value);

            document.getElementById('close').click();
          }
        }

        function uploadCv(){
            if (localStorage.getItem('user_id')) {
                if (selectedFile.value) {
                    CareerApiService.file = selectedFile.value;
                    CareerApiService.jobTitle = jobTitle.value;
                    CareerApiService.jobId = jobId.value;
                    router.push({name:'dashboard-cv'})
                }
                else {
                    error.value = 'Please upload the file first'
                }
            }
            else {
              router.push({name:'signin'})
            }
        }

        // The bottom CTA can't submit anything by itself (no file attached yet
        // down here) — it sends the user back up to the hero, where the actual
        // upload + job-title controls live.
        function goToEntry() {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }

        function setJobTitle(item){
            jobTitle.value = item.name
            jobId.value = item.id
            CareerApiService.jobTitle = item.name
            CareerApiService.jobId = item.id
            setTimeout(() => {
                showdropdown.value = false
            }, 500);
        }

        watch(jobTitle,(e)=>{
            if (e=='') {
                jobs.value = []
                showdropdown.value = false;
            }
            else {
                jobs.value = [];
                CareerApiService.OccupSearch(e).then((res)=>{
                    showdropdown.value = true;
                    for (let i = 0; i < res.data.jobs.length; i++) {
                        jobs.value.push(res.data.jobs[i])
                    }
                })
            }
        })

        const error = ref();

        return {
            router,
            selectedFile,
            preview,
            error,
            spinner,
            jobTitle,
            jobId,
            showdropdown,
            jobs,
            stats,
            roleMap,
            resolved,
            readyColor,
            translateDemo,
            gapDemo,
            whyItems,
            popularJobs,
            categories,
            PASTELS,
            categoryIcon: name => CATEGORY_ICONS[name] || 'fa-solid fa-briefcase',
            handleDrop,
            handleFileSelect,
            uploadCv,
            goToEntry,
            setJobTitle,
        }
    }
})
</script>
<style lang="scss" scoped>
/* ---------------------------------------------------------------- hero */
.tp-hero {
    position: relative;
    min-height: calc(100dvh - 60px);
    display: flex;
    align-items: center;
    padding: 48px 24px;
    overflow: hidden;
}

.tp-hero__inner {
    position: relative;
    z-index: 1;
    width: 100%;
}

.tp-hero__grid {
    display: grid;
    grid-template-columns: 1.06fr 1fr;
    gap: 52px;
    align-items: center;
}

@media (max-width: 1080px) {
    .tp-hero__grid { grid-template-columns: 1fr; gap: 44px; }
    .tp-hero__panel { display: none; }
}

/* ---------------------------------------------------------------- entry
   Explicit z-index (not auto) on the row: several descendants carry .tm-reveal,
   whose animation implicitly promotes them to their own stacking context, which
   would otherwise let a later sibling paint over this row's dropdown regardless
   of the dropdown's own z-index. */
.tp-entry {
    position: relative;
    z-index: 5;
    margin-top: 4px;
}

.tp-entry__row {
    position: relative;
    display: flex;
    align-items: center;
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-sm);
    box-shadow: var(--tm-shadow-md);
    padding: 8px 8px;
    transition: border-color 0.15s var(--tm-ease), box-shadow 0.15s var(--tm-ease);
}

.tp-entry__row:focus-within {
    border-color: var(--tm-border-focus);
    box-shadow: 0 0 0 3px var(--tm-accent-glow);
}

.tp-entry__input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: var(--tm-font-sans);
    font-size: 1rem;
    color: var(--tm-ink);
    padding: 16px 12px;
    min-width: 0;
}

.tp-entry__input::placeholder { color: var(--tm-ink-faint); }

.tp-entry__divider {
    width: 1px;
    align-self: stretch;
    margin: 8px 0;
    background: var(--tm-border);
    flex: none;
}

.tp-entry__upload {
    border: none;
    background: transparent;
    padding: 0 18px;
    font-family: var(--tm-font-sans);
    font-size: 0.88rem;
    font-weight: 500;
    color: var(--tm-ink-muted);
    cursor: pointer;
    white-space: nowrap;
    flex: none;
    transition: color 0.15s var(--tm-ease);
}

.tp-entry__upload:hover { color: var(--tm-ink); }

.tp-entry__go { padding: 15px 28px; font-size: 1rem; flex: none; }

@media (max-width: 640px) {
    .tp-entry__row { flex-wrap: wrap; gap: 8px; padding: 12px; }
    .tp-entry__input { flex-basis: 100%; order: -1; }
    .tp-entry__divider { display: none; }
    .tp-entry__upload { padding: 0; }
    .tp-entry__go { margin-left: auto; }
}

/* ----------------------------------------------------------- hero panel */
.tp-hero__panel { position: relative; }

.tp-panel-glow {
    position: absolute;
    inset: -12% -8% -18% -8%;
    background: radial-gradient(ellipse at 50% 45%,
                rgba(22, 104, 227, 0.13) 0%,
                rgba(14, 159, 110, 0.09) 42%,
                transparent 72%);
    filter: blur(8px);
    pointer-events: none;
}

/* ------------------------------------------------------------------ KPIs */
.tp-kpis {
    padding: 0 24px 64px;
}

.tp-kpis__grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    padding-top: 30px;
    border-top: 1px solid var(--tm-border);
}

@media (max-width: 720px) {
    .tp-kpis__grid { grid-template-columns: 1fr 1fr; gap: 28px 24px; }
}

/* Every framed block on this page (cards, inputs) insets its content from
   its own edge — a card's text sits 26px inside its border, never flush
   against it. The first KPI number used to sit flush at the grid's left
   edge (0 inset), which reads as pulled left relative to everything else;
   giving every item, including the first, the same 26px inset as a card's
   padding lines the numbers up with where card text actually starts, not
   where the card's outer border is. */
.tp-kpi {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-left: 26px;
}

.tp-kpi:not(:first-child) {
    border-left: 1px solid var(--tm-border);
}

@media (max-width: 720px) {
    .tp-kpi:nth-child(2n+1) { border-left: none; }
}

.tp-kpi__num {
    font-size: 1.9rem;
    font-weight: 600;
    color: var(--tm-ink);
    letter-spacing: -0.02em;
    line-height: 1;
}

.tp-kpi__label { font-size: 0.86rem; color: var(--tm-ink-muted); }

/* ------------------------------------------------------------------- why */
.tp-sec__head { margin-bottom: 40px; }

.tp-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
}

@media (max-width: 940px) { .tp-cards { grid-template-columns: 1fr 1fr; } }
@media (max-width: 620px) { .tp-cards { grid-template-columns: 1fr; } }

.tp-vcard {
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-md);
    padding: 26px;
    box-shadow: var(--tm-shadow-sm);
    transition: border-color 0.18s var(--tm-ease),
                transform 0.18s var(--tm-ease),
                box-shadow 0.18s var(--tm-ease);
}

.tp-vcard:hover {
    border-color: var(--tm-border-strong);
    transform: translateY(-2px);
    box-shadow: var(--tm-shadow-md);
}

.tp-vcard__icon { margin-bottom: 18px; }

.tp-vcard__title {
    font-size: 1.03rem;
    font-weight: 700;
    margin-bottom: 8px;
}

.tp-vcard__text {
    font-size: 0.9rem;
    line-height: 1.6;
}

/* ------------------------------------------------------------------- how */

.tp-beats { display: flex; flex-direction: column; gap: 30px; }

.tp-beat {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 48px;
    align-items: center;
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-lg);
    box-shadow: var(--tm-shadow-sm);
    padding: 44px;
}

@media (max-width: 900px) {
    .tp-beat { grid-template-columns: 1fr; gap: 30px; padding: 30px; }
    .tp-beat--flip .tp-beat__side { order: 0; }
}

.tp-beat--flip .tp-beat__side { order: 2; }

.tp-beat__num {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    color: var(--tm-accent);
    margin-bottom: 14px;
}

.tp-beat__name {
    font-size: clamp(20px, 2.2vw, 27px);
    font-weight: 700;
    margin-bottom: 12px;
}

.tp-beat__side p { font-size: 0.97rem; max-width: 44ch; }

/* demo panels inside the story beats */
.tp-demo {
    background: var(--tm-surface-sunk);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-md);
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.tp-demo--drop {
    align-items: center;
    text-align: center;
    gap: 8px;
    padding: 40px 22px;
    border-style: dashed;
    border-color: var(--tm-border-strong);
}

.tp-demo__icon { font-size: 1.6rem; color: var(--tm-accent); margin-bottom: 6px; }
.tp-demo__file { font-size: 0.86rem; color: var(--tm-ink); font-weight: 500; }
.tp-demo__sub { font-size: 0.78rem; color: var(--tm-ink-faint); }

.tp-demo__line {
    display: grid;
    grid-template-columns: 1fr 20px 1fr;
    align-items: center;
    gap: 10px;
    font-size: 0.82rem;
}

.tp-demo__arrow { color: var(--tm-ink-faint); text-align: center; }
.tp-demo__term { color: var(--tm-ink); font-weight: 500; }

.tp-demo__role {
    display: grid;
    grid-template-columns: 1fr 70px 40px;
    align-items: center;
    gap: 12px;
}

.tp-demo__role-name { font-size: 0.87rem; color: var(--tm-ink); font-weight: 500; }
.tp-demo__pct { font-size: 0.8rem; font-weight: 600; text-align: right; }

.tp-demo__gap {
    display: grid;
    grid-template-columns: 22px 1fr auto;
    align-items: center;
    gap: 12px;
}

.tp-demo__rank { font-size: 0.74rem; color: var(--tm-ink-faint); }
.tp-demo__gap-name { font-size: 0.87rem; color: var(--tm-ink); font-weight: 500; }
.tp-demo__gap-name::first-letter { text-transform: uppercase; }

.tp-how__cta {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    margin-top: 52px;
}


/* ------------------------------------------------- translation panel */
.tp-translate {
    background: var(--tm-surface);
    border: 1px solid var(--tm-border);
    border-radius: var(--tm-radius-lg);
    box-shadow: var(--tm-shadow-lg);
    overflow: hidden;
}

.tp-translate__bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 13px 16px;
    border-bottom: 1px solid var(--tm-border);
    background: var(--tm-surface-sunk);
}

.tp-translate__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--tm-border-strong);
}

.tp-translate__file {
    margin-left: 6px;
    font-size: 12px;
    color: var(--tm-ink-muted);
}

.tp-translate__badge { margin-left: auto; }

.tp-rrow {
    display: grid;
    grid-template-columns: 1fr 84px 44px;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--tm-border-soft);
    opacity: 0;
    transform: translateY(6px);
    transition: opacity 0.45s var(--tm-ease), transform 0.45s var(--tm-ease);
}

.tp-rrow.is-resolved { opacity: 1; transform: none; }

.tp-rrow__name {
    font-size: 13px;
    font-weight: 500;
    color: var(--tm-ink);
    line-height: 1.3;
}

.tp-rrow__meter .tm-meter__fill { transition: width 0.8s var(--tm-ease); }

.tp-rrow__pct {
    font-size: 12.5px;
    font-weight: 600;
    text-align: right;
}

.tp-translate__foot {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 13px 18px;
    background: var(--tm-surface-sunk);
    border-top: 1px solid var(--tm-border);
}

.tp-translate__foot-text {
    font-size: 12px;
    color: var(--tm-ink-muted);
}

@media (prefers-reduced-motion: reduce) {
    .tp-rrow { opacity: 1; transform: none; transition: none; }
}

.tm-hero-title {
    font-size: clamp(38px, 5vw, 60px);
    font-weight: 800;
    line-height: 1.08;
    margin: 20px 0 42px;
}

.tm-hero-sub {
    max-width: 46ch;
    margin: 20px 0 28px;
}

.tm-h2 {
    font-weight: 700;
    letter-spacing: -0.03em;
    color: var(--tm-ink);
    font-size: 2rem;
}

.tm-search__row {
    display: flex;
    align-items: center;
    gap: 10px;
    position: relative;
    flex-wrap: wrap;
}

.tm-search__icon {
    color: var(--tm-ink-muted);
    margin-left: 10px;
}

.tm-search__input {
    border: none;
    outline: none;
    flex: 1;
    min-width: 140px;
    padding: 10px 0;
    font-family: var(--tm-font-sans);
    color: var(--tm-ink);
    background: transparent;
}

.tm-search__divider {
    width: 1px;
    align-self: stretch;
    background: var(--tm-border);
}

.tm-search__upload {
    background: transparent;
    border: none;
    color: var(--tm-ink-muted);
    font-family: var(--tm-font-sans);
    font-size: 0.9rem;
    padding: 10px 4px;
    white-space: nowrap;
}

.tm-suggestion {
    margin-top: 14px;
    font-size: 0.85rem;
}

.tm-stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 767px) { grid-template-columns: repeat(2, 1fr); }
}

.tm-stat {
    padding: 22px;
}

.tm-stat__icon {
    width: 34px;
    height: 34px;
    padding: 0;
    justify-content: center;
    margin-bottom: 14px;
}

.tm-stat__num {
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--tm-ink);
    margin-bottom: 2px;
}

.tm-job-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 48px;
    @media (max-width: 767px) { grid-template-columns: 1fr; }
}

.tm-job-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    color: inherit;
    text-decoration: none;
}
.tm-job-row__name { color: var(--tm-ink); font-weight: 500; }
.tm-job-row__name::first-letter { text-transform: uppercase; }
.tm-job-row:hover .tm-job-row__name { color: var(--tm-accent); }

.tp-sec__bar { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
.tp-sec__all { text-decoration: none; }

.tm-category-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 991px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 575px) { grid-template-columns: 1fr; }
}

.tm-category {
    display: flex;
    align-items: center;
    padding: 20px;
    color: inherit;
    text-decoration: none;
}

.tm-category__icon {
    width: 40px;
    height: 40px;
    justify-content: center;
    flex-shrink: 0;
}

.tm-employer {
    text-align: center;
    padding: 48px;
}

.tm-modal {
    background: var(--tm-surface);
    color: var(--tm-ink-soft);
    border-radius: var(--tm-radius-md);
    border: 1px solid var(--tm-border);
}

.tm-modal :deep(.modal-header),
.tm-modal :deep(.modal-footer) {
    border-color: var(--tm-border);
}

.tm-modal :deep(.modal-title) {
    color: var(--tm-ink);
}

.tm-dropzone {
    border: 1px dashed var(--tm-border);
    border-radius: 8px;
    padding: 40px 20px;
    cursor: pointer;
}

.tm-dropzone__icon {
    color: var(--tm-ink-muted);
    font-size: 1.4rem;
}
</style>
